import axios from "axios";
import { useCallback, useRef, useState } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { DCDesignListing, DCDesignListingImage, DCDesignMockup, ListingProductType } from "./types";

const orderHandlerUrl = environment.baseUrl;

const gqlEndpoint = `${environment.baseUrl}/graphql`;

const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
  const { data } = await axios.post(gqlEndpoint, { query, variables }, {
    headers: { "X-Wallmates-Auth": authToken }
  });
  if (data.errors) throw new Error(data.errors[0]?.message || "GraphQL error");
  return data.data;
};

export const useDesignListingImages = () => {
  const [, setSnackbar] = useSnackbar();
  const [, authToken, loginFunctions] = useLogin();
  const [listingImages, setListingImages] = useState<DCDesignListingImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  /**
   * Loads listing images for ONE listing, or for all of them when productType is
   * omitted (which is what the SEO step wants — it shows both galleries together).
   *
   * `listingImages` state is shared, so two steps mounted at once would clobber each
   * other. They aren't: the wizard renders exactly one listing-image sub-step at a
   * time, and each mount re-fetches for its own product type.
   */
  const listListingImages = useCallback(async (designId: number, productType?: ListingProductType) => {
    if (!designId) return null;
    try {
      setLoading(true);
      const q = `query($design_id:ID!, $product_type:String){ dcDesignListingImages(design_id:$design_id, product_type:$product_type){ id design_id product_type source_type source_id source_url position alt_text seo_filename seo_completed_at created_at updated_at } }`;
      const data = await graphqlRequest(authToken, q, { design_id: designId, product_type: productType ?? null });
      const next = (data?.dcDesignListingImages || []) as DCDesignListingImage[];
      const sorted = [...next].sort((a, b) => a.position - b.position);
      setListingImages(sorted);
      return sorted;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Failed to load listing images." });
      return null;
    } finally {
      setLoading(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  /**
   * Replaces the image set for ONE listing. The backend scopes its delete-then-insert
   * to the same product_type, so saving the wrapping-paper gallery leaves the
   * wallpaper gallery untouched — and vice versa.
   */
  const replaceListingImages = useCallback(async (
    designId: number,
    images: { source_type: string; source_id: number; position: number; source_url?: string }[],
    productType: ListingProductType = "wallpaper"
  ) => {
    if (!designId) return null;
    try {
      setSaving(true);
      const mutation = `mutation($design_id:ID!, $images:[DCDesignListingImageInput!]!, $product_type:String){ replaceDCDesignListingImages(design_id:$design_id, images:$images, product_type:$product_type){ id design_id product_type source_type source_id source_url position alt_text seo_filename seo_completed_at created_at updated_at } }`;
      const data = await graphqlRequest(authToken, mutation, { design_id: designId, images, product_type: productType });
      const next = (data?.replaceDCDesignListingImages || []) as DCDesignListingImage[];
      const sorted = [...next].sort((a, b) => a.position - b.position);
      setListingImages(sorted);
      return sorted;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Failed to save listing images." });
      return null;
    } finally {
      setSaving(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  const uploadManualMockup = useCallback(async (designId: number, file: File): Promise<DCDesignMockup | null> => {
    if (!designId || !file) return null;
    try {
      // Step 1: Get presigned upload URL
      const uploadMutation = `mutation($input:DCDesignManualMockupUploadInput!){ createDCDesignManualMockupUpload(input:$input){ upload_url public_url } }`;
      const uploadData = await graphqlRequest(authToken, uploadMutation, {
        input: { design_id: designId, file_name: file.name }
      });
      const { upload_url, public_url } = uploadData?.createDCDesignManualMockupUpload || {};
      if (!upload_url || !public_url) throw new Error("Failed to get upload URL");

      // Step 2: Upload file to presigned URL
      await axios.put(upload_url, file, {
        headers: { "Content-Type": file.type || "image/jpeg" }
      });

      // Step 3: Complete the upload (server generates thumbnail + creates DB record)
      const completeMutation = `mutation($design_id:ID!, $mockup_path:String!, $file_name:String!){ completeDCDesignManualMockupUpload(design_id:$design_id, mockup_path:$mockup_path, file_name:$file_name){ id design_id mockup_template_id status thumbnail_path mockup_path manual_upload created_at updated_at } }`;
      const completeData = await graphqlRequest(authToken, completeMutation, {
        design_id: designId,
        mockup_path: public_url,
        file_name: file.name
      });
      return (completeData?.completeDCDesignManualMockupUpload as DCDesignMockup) || null;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      throw err;
    }
  }, [authToken, loginFunctions.logout]);

  const deleteManualMockup = useCallback(async (mockupId: number) => {
    if (!mockupId) return null;
    try {
      const mutation = `mutation($id:ID!){ deleteDCDesignManualMockup(id:$id){ id design_id } }`;
      const data = await graphqlRequest(authToken, mutation, { id: mockupId });
      return data?.deleteDCDesignManualMockup || null;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Failed to delete image." });
      return null;
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  return {
    listingImages,
    loading,
    saving,
    uploading,
    setUploading,
    listListingImages,
    replaceListingImages,
    uploadManualMockup,
    deleteManualMockup,
  };
};

// #region useListingImageSeo
export const useListingImageSeo = () => {
  const [, setSnackbar] = useSnackbar();
  const [, authToken, loginFunctions] = useLogin();
  const [generating, setGenerating] = useState(false);
  const [completing, setCompleting] = useState(false);
  const debounceTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const generateSeo = useCallback(async (designId: number): Promise<DCDesignListingImage[] | null> => {
    if (!designId) return null;
    try {
      setGenerating(true);
      const response = await axios.post(
        `${orderHandlerUrl}/api/v1/designs/image-seo/${designId}/generate`,
        {},
        { headers: { "x-wallmates-auth": authToken } }
      );
      return response.data as DCDesignListingImage[];
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.response?.data?.message || "Failed to generate image SEO." });
      return null;
    } finally {
      setGenerating(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  const updateImageSeo = useCallback(async (designId: number, imageId: number, data: { alt_text?: string; seo_filename?: string }): Promise<DCDesignListingImage | null> => {
    if (!imageId) return null;
    try {
      const response = await axios.put(
        `${orderHandlerUrl}/api/v1/designs/image-seo/${designId}/images/${imageId}`,
        data,
        { headers: { "x-wallmates-auth": authToken } }
      );
      return response.data as DCDesignListingImage;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.response?.data?.message || "Failed to update image SEO." });
      return null;
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  const updateImageSeoDebounced = useCallback((designId: number, imageId: number, data: { alt_text?: string; seo_filename?: string }) => {
    if (debounceTimers.current[imageId]) {
      clearTimeout(debounceTimers.current[imageId]);
    }
    debounceTimers.current[imageId] = setTimeout(() => {
      updateImageSeo(designId, imageId, data);
      delete debounceTimers.current[imageId];
    }, 800);
  }, [updateImageSeo]);

  const completeSeo = useCallback(async (designId: number): Promise<boolean> => {
    if (!designId) return false;
    try {
      setCompleting(true);
      await axios.post(
        `${orderHandlerUrl}/api/v1/designs/image-seo/${designId}/complete`,
        {},
        { headers: { "x-wallmates-auth": authToken } }
      );
      return true;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.response?.data?.message || "Failed to complete SEO setup." });
      return false;
    } finally {
      setCompleting(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  return {
    generating,
    completing,
    generateSeo,
    updateImageSeo,
    updateImageSeoDebounced,
    completeSeo,
  };
};

// #region useDesignListings
/**
 * The design's published Shopify listings (one row per product type).
 *
 * Separate from `useDesignListingImages` on purpose: that hook is about the images
 * a listing WILL use, this one is about the products that actually exist on
 * Shopify. The Release step reads this to link out to both products.
 */
export const useDesignListings = () => {
  const [, authToken, loginFunctions] = useLogin();
  const [listings, setListings] = useState<DCDesignListing[]>([]);
  const [loading, setLoading] = useState(false);

  const listDesignListings = useCallback(async (designId: number) => {
    if (!designId) return null;
    try {
      setLoading(true);
      const q = `query($design_id:ID!){ dcDesignListings(design_id:$design_id){ id design_id product_type shopify_product_id short_product_id status created_at updated_at } }`;
      const data = await graphqlRequest(authToken, q, { design_id: designId });
      const next = (data?.dcDesignListings || []) as DCDesignListing[];
      setListings(next);
      return next;
    } catch (err: any) {
      // Intentionally silent: the listing links are a convenience on an
      // already-successful release. A failure here must not surface as an error
      // toast on a release that worked.
      errorCatcher(err, loginFunctions.logout);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authToken, loginFunctions.logout]);

  return { listings, loading, listDesignListings };
};
